import type { components } from '@/generated/contracts/api-v1';
import { FE_CHANNELS, BE_TO_FE_CHANNEL } from './feChannelConfig';
import type { CellState, ChannelKey, FeatureInfo, Matrix, SizeThreshold } from './types';

/** What getPublishingMatrix serves. */
export type BackendPublishingMatrix = components['schemas']['PublishingMatrixDto'];

const FEATURE_LABELS: Record<string, string> = {
  place_anywhere: 'No anchor',
  image_tracking: 'Image anchor',
  geospatial: 'Map anchor',
  immersal: 'Immersal scan',
  pinch_trigger: 'Pinch trigger',
  complex_stock_animation: 'Complex animation',
};

function humanizeFeatureKey(key: string): string {
  return FEATURE_LABELS[key] ?? key.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

function normalizeGroup(group: string): FeatureInfo['group'] {
  if (group === 'triggers' || group === 'playback') return group;
  return 'anchoring';
}

export function transformBeMatrix(raw: BackendPublishingMatrix): Matrix {
  const sizeThresholds = {} as Record<ChannelKey, SizeThreshold>;
  for (const t of raw.sizeThresholds) {
    const feKey = BE_TO_FE_CHANNEL[t.channel];
    if (!feKey) continue;
    sizeThresholds[feKey] = { warnMB: t.warnMb, hard: t.hard };
  }

  const cells: Matrix['cells'] = {};
  for (const cell of raw.cells) {
    const row: Partial<Record<ChannelKey, CellState>> = cells[cell.feature] ?? {};
    cells[cell.feature] = row;
    const feKey = BE_TO_FE_CHANNEL[cell.channel];
    if (feKey) row[feKey] = cell.state as CellState;
  }

  const features: FeatureInfo[] = raw.features
    .filter((f) => f.key in FEATURE_LABELS)
    .map((f) => ({
      key: f.key,
      label: humanizeFeatureKey(f.key),
      group: normalizeGroup(f.group),
    }));

  return {
    channels: FE_CHANNELS,
    features,
    cells,
    sizeThresholds,
  };
}
